import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { downloadResponseFile } from '../../utils/downloadFile';
import { formatPreferredDate } from '../../utils/preferredDate';
import { formatPackageDuration } from '../../utils/packageDuration';
import {
  adjustQuotedTotals,
  formatQuoteMoney,
  fromMinor,
  toMinor,
  priceQuote,
  PricedLine,
  type PricedLineInput,
  type QuotePricingSnapshot,
} from '../../utils/quotePricing';
import { LetterFlow } from '../Quote/LetterText';
import { parseQuoteLetter } from '../../utils/quoteLetter';
import ClientSendPrompt from './ClientSendPrompt';
import LetterComposer from './LetterComposer';
import './QuoteBuilder.css';
import ClockTimeField from '../Common/ClockTimeField';
import PreferredDateField from '../Common/PreferredDateField';
import ShootLocationField, {
  normalizeShootLocation,
  type ShootLocation,
} from '../Common/ShootLocationField';
import { clientPaymentLink, clientQuoteLink } from '../../utils/quoteShare';
import { paymentStateLabel, studioDate } from '../../utils/studioDate';
import { clientFacingShootType, StoredQuote } from '../../utils/quoteRecord';
import { openWhatsAppHandoff } from '../../utils/studioNotifyChannels';
import { convertCurrencyWithMarkup, loadCurrencySettings } from '../../utils/CurrencyUtils';
import { preferredFromSessions, type QuoteSession } from '../../utils/quoteSessions';

export interface QuoteEditorHandle {
  readonly dirty: boolean;
  readonly busy: boolean;
  readonly error: string;
  save: () => Promise<boolean>;
  discard: () => void;
}

interface Summary {
  lockedMinor: number;
  outstandingMinor: number;
  receivedMinor?: number;
  currentlyDueMinor?: number;
  depositDueMinor?: number;
  depositExpectedMinor?: number;
  depositReceivedMinor?: number;
  figures?: { overdueMinor?: number };
  payments: Array<{ id: string; type: string; status: string; verifiedMinor: number; note?: string; paidAt?: string | null }>;
}

function placeLine(location?: { name?: string; address?: string }) {
  const name = location?.name?.trim() || '';
  const address = location?.address?.trim() || '';
  if (name && address && name !== address) return `${name}, ${address}`;
  return name || address;
}

function sessionForLine(sessions: QuoteSession[] | undefined, line: PricedLine): QuoteSession | undefined {
  const rows = Array.isArray(sessions) ? sessions : [];
  return rows.find((session) => session.id === line.sessionId || session.id === line.id);
}

function dayPlaceLines(sessions: QuoteSession[] | undefined): string[] {
  const rows = Array.isArray(sessions) ? sessions : [];
  if (rows.length < 2) return [];
  return rows.map((session, index) => {
    const label = session.label?.trim() || `Day ${index + 1}`;
    const when = [session.date ? formatPreferredDate(session.date) : '', session.startTime || ''].filter(Boolean).join(' at ');
    const place = placeLine(session.location || undefined) || 'Place to be decided';
    return [label, when, place].filter(Boolean).join(' · ');
  });
}

function lineDetail(line: PricedLine): string {
  const phrase = line.kind === 'package' || line.kind === 'session'
    ? formatPackageDuration(line.durationMinutes)
    : '';
  const coverage = phrase ? `Duration: ${phrase}` : '';
  const when = line.kind === 'session'
    ? [line.date ? formatPreferredDate(line.date) : '', line.startTime || ''].filter(Boolean).join(' at ')
    : '';
  const pricing = line.pricingType === 'per-hour'
    ? `${line.units} hour${line.units === 1 ? '' : 's'}`
    : line.pricingType === 'per-each' && line.units > 1
      ? `× ${line.units}`
      : '';
  return [when, coverage, pricing].filter(Boolean).join(' · ');
}

function daySummary(line: PricedLine, draft: LineDraft | undefined, sessions?: QuoteSession[]) {
  const date = draft ? draft.date.trim() : (line.date || '');
  const time = draft ? draft.time.trim() : (line.startTime || '');
  const durationMinutes = draft && draft.duration.trim() !== '' ? Number(draft.duration) : line.durationMinutes;
  const duration = formatPackageDuration(Number.isFinite(Number(durationMinutes)) ? Number(durationMinutes) : null);
  const when = [date ? formatPreferredDate(date) : '', time].filter(Boolean).join(' at ');
  const place = placeLine(draft?.location) || placeLine(sessionForLine(sessions, line)?.location || undefined);
  const name = (draft?.name || '').trim() || line.name;
  const description = (draft ? draft.description : (line.description || '')).trim();
  const features = draft
    ? draft.features.split('\n').map((entry) => entry.trim()).filter(Boolean)
    : (line.features || []);
  return {
    name,
    detail: [when || 'Date to be decided', duration ? `Duration: ${duration}` : '', place || 'Place to be decided'].filter(Boolean).join(' · '),
    description,
    features,
  };
}

function dayAmount(line: PricedLine, draft: LineDraft | undefined, currency: string): string {
  if (line.complimentary && !(draft && draft.price.trim())) return 'Included';
  const major = draft ? Number(draft.price) : NaN;
  if (draft && draft.price.trim() && Number.isFinite(major)) {
    const units = line.units > 0 ? line.units : 1;
    return formatQuoteMoney(toMinor(major * units), currency);
  }
  return formatQuoteMoney(line.quoteExtendedMinor, currency);
}

function QuoteLetter({ text }: { text: string }) {
  const pages = parseQuoteLetter(text);
  if (!pages.length) return null;
  return (
    <div className="studio-quote-letter">
      {pages.map((page, pageIndex) => (
        <div key={pageIndex}>
          {page.map((paragraph, paragraphIndex) => (
            <LetterFlow lines={paragraph} key={paragraphIndex} />
          ))}
        </div>
      ))}
    </div>
  );
}

function lineToInput(line: PricedLine, priceMajorOverride?: number): PricedLineInput {
  const units = line.units > 0 ? line.units : 1;
  const major = priceMajorOverride != null
    ? priceMajorOverride
    : fromMinor(line.sourceExtendedMinor || line.quoteExtendedMinor) / units;
  const hasOverride = priceMajorOverride != null;
  return {
    id: line.id,
    kind: line.kind,
    name: line.name,
    description: line.description,
    features: line.features,
    durationMinutes: line.durationMinutes,
    sessionId: line.sessionId,
    date: line.date,
    startTime: line.startTime,
    pricingType: line.pricingType || 'fixed',
    units,
    unitAmount: major,
    sourceCurrency: line.sourceCurrency || 'R',
    overrideUnitAmount: hasOverride ? major : null,
    overrideCurrency: hasOverride ? (line.sourceCurrency || 'R') : null,
    complimentary: hasOverride ? false : line.complimentary,
  };
}

function syncSessionsFromLines(sessions: QuoteSession[] | undefined, lines: PricedLine[]): QuoteSession[] | undefined {
  if (!Array.isArray(sessions) || sessions.length === 0) return sessions;
  return sessions.map((session) => {
    const line = lines.find((entry) => (
      entry.kind === 'session'
      && (entry.sessionId === session.id || entry.id === session.id)
    ));
    if (!line) return session;
    return {
      ...session,
      label: line.name || session.label,
      date: line.date || session.date || '',
      startTime: line.startTime || session.startTime || '',
      durationMinutes: line.durationMinutes != null ? line.durationMinutes : session.durationMinutes,
      notes: line.description || '',
      priceMajor: fromMinor(line.sourceExtendedMinor || line.quoteExtendedMinor),
    };
  });
}

interface LineDraft {
  id: string;
  name: string;
  description: string;
  features: string;
  price: string;
  date: string;
  time: string;
  duration: string;
  location: ShootLocation;
}

interface QuoteFormState {
  letter: string;
  whenDate: string;
  whenTime: string;
  whenLocation: ShootLocation;
  discount: string;
  deposit: string;
  lines: LineDraft[];
}

function lineDraftFrom(line: PricedLine, sessions?: QuoteSession[]): LineDraft {
  const units = line.units > 0 ? line.units : 1;
  const session = line.kind === 'session' ? sessionForLine(sessions, line) : undefined;
  return {
    id: line.id,
    name: line.name || '',
    description: line.description || '',
    features: (line.features || []).join('\n'),
    price: line.complimentary ? '' : String(fromMinor(line.sourceExtendedMinor || line.quoteExtendedMinor) / units),
    date: line.date || '',
    time: line.startTime || '',
    duration: line.durationMinutes != null ? String(line.durationMinutes) : '',
    location: normalizeShootLocation(session?.location),
  };
}

function formFromQuote(quote: StoredQuote): QuoteFormState {
  return {
    letter: quote.client?.message || quote.draft?.personalMessage || '',
    whenDate: quote.preferredDate || '',
    whenTime: quote.preferredTime || '',
    whenLocation: normalizeShootLocation(quote.preferredLocation),
    discount: String(quote.snapshot?.discountPercent || 0),
    deposit: String(quote.snapshot?.depositPercent ?? 50),
    lines: (quote.snapshot?.lines || []).map((line) => lineDraftFrom(line, quote.sessions)),
  };
}

function formKey(form: QuoteFormState): string {
  return JSON.stringify(form);
}

/** A half-typed percent keeps the last saved share until the number is complete. */
function readPercent(value: string, fallback: number): number {
  const trimmed = value.trim();
  if (!/^\d+(\.\d*)?$/.test(trimmed)) return fallback;
  const number = Number(trimmed.endsWith('.') ? trimmed.slice(0, -1) || '0' : trimmed);
  return Number.isFinite(number) ? number : fallback;
}

function termsWithDeposit(terms: string, percent: number): string {
  const shown = Number.isInteger(percent) ? String(percent) : String(Math.round(percent * 100) / 100);
  return terms.replace(
    /A deposit of [\d.]+% secures the date\./,
    `A deposit of ${shown}% secures the date.`,
  );
}

function draftToInput(line: PricedLine, draft: LineDraft): PricedLineInput | null {
  const duration = draft.duration.trim() === '' ? null : Number(draft.duration);
  if (duration != null && (!Number.isFinite(duration) || duration < 0)) return null;
  const priceText = draft.price.trim();
  const price = priceText === '' ? null : Number(priceText);
  if (priceText !== '' && (!Number.isFinite(price) || (price as number) < 0)) return null;
  const features = draft.features.split('\n').map((entry) => entry.trim()).filter(Boolean);
  const input = price != null ? lineToInput(line, price) : lineToInput(line);
  return {
    ...input,
    name: draft.name.trim() || line.name,
    description: draft.description.trim(),
    features: features.length ? features : undefined,
    date: draft.date.trim() || undefined,
    startTime: draft.time.trim() || undefined,
    durationMinutes: duration == null ? line.durationMinutes : duration,
    complimentary: price == null ? line.complimentary : false,
  };
}

function sessionsWithPlaces(sessions: QuoteSession[] | undefined, lines: PricedLine[], drafts: LineDraft[]): QuoteSession[] {
  const synced = syncSessionsFromLines(sessions, lines) || [];
  return synced.map((session) => {
    const line = lines.find((entry) => (
      entry.kind === 'session'
      && (entry.sessionId === session.id || entry.id === session.id)
    ));
    const draft = line ? drafts.find((entry) => entry.id === line.id) : undefined;
    if (!draft) return session;
    return { ...session, location: normalizeShootLocation(draft.location) };
  });
}

/**
 * The quotation as one editable page. Every field is open.
 * One save, near the sticky header, writes the whole form.
 */
const StudioQuoteRecord = ({
  quote,
  summary,
  askForReceipt = 0,
  /** quote = scrollable editable quotation only; deposit = record payment; full = legacy combined. */
  variant = 'full',
  onReceiptRecorded,
  onOpenHistory,
  onGuidesUpdated,
  onQuoteUpdated,
  onEditorChange,
}: {
  quote: StoredQuote;
  summary?: Summary;
  /** Increments when the shoot file asks to record a payment. */
  askForReceipt?: number;
  variant?: 'quote' | 'deposit' | 'full';
  onReceiptRecorded?: () => void;
  onOpenHistory?: () => void;
  onGuidesUpdated?: () => void;
  onQuoteUpdated?: (quote: StoredQuote) => void;
  /** The open quotation form, so the shoot file can save it and block leaving. */
  onEditorChange?: (editor: QuoteEditorHandle | null) => void;
}) => {
  const showQuoteArticle = variant === 'quote' || variant === 'full';
  const showGuides = variant === 'full';
  const showPayments = variant === 'deposit' || variant === 'full';
  const quoteEditable = showQuoteArticle;
  const [note, setNote] = useState('');
  const [receiptAmount, setReceiptAmount] = useState('');
  const [receiptDate, setReceiptDate] = useState('');
  const [receiptNote, setReceiptNote] = useState('');
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState('');
  const [pdfBusy, setPdfBusy] = useState<'download' | 'email' | ''>('');
  const [guideLibrary, setGuideLibrary] = useState<Array<{ id: string; title: string }>>([]);
  const [guideIds, setGuideIds] = useState<string[]>(Array.isArray(quote.guideIds) ? quote.guideIds : []);
  const [guidesBusy, setGuidesBusy] = useState<'save' | 'email' | 'whatsapp' | ''>('');
  const [issueBusy, setIssueBusy] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');
  const [editingTimeId, setEditingTimeId] = useState<string | null>(null);
  const [editingWhenTime, setEditingWhenTime] = useState(false);
  const [expandedDayId, setExpandedDayId] = useState<string | null>(null);
  const [form, setForm] = useState<QuoteFormState>(() => formFromQuote(quote));
  const [baseline, setBaseline] = useState(() => formKey(formFromQuote(quote)));
  const formRef = useRef(form);
  const baselineRef = useRef(baseline);
  const dirtyRef = useRef(false);
  const busyRef = useRef(false);
  const editErrorRef = useRef('');
  const quoteRef = useRef(quote);
  formRef.current = form;
  baselineRef.current = baseline;
  dirtyRef.current = formKey(form) !== baseline;
  busyRef.current = editBusy;
  editErrorRef.current = editError;
  quoteRef.current = quote;

  const snapshot = quote.snapshot;
  const settlementRate = snapshot.settlementRate?.exchangeRate
    || snapshot.rates?.find((item) => item.fromCurrency === 'R' && item.toCurrency === snapshot.currency && Number(item.exchangeRate) > 0)?.exchangeRate
    || null;
  const preview = adjustQuotedTotals({
    currency: snapshot.currency,
    quoteSubtotalMinor: snapshot.quoteSubtotalMinor,
    discountPercent: readPercent(form.discount, Number(snapshot.discountPercent) || 0),
    depositPercent: readPercent(form.deposit, snapshot.depositPercent ?? 50),
    settlementRate,
  });
  const packageName = snapshot.lines.find((line) => line.kind === 'package')?.name || clientFacingShootType(quote.shootType) || 'Quotation';
  const publicOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const quoteLink = quote.clientToken ? clientQuoteLink(publicOrigin, quote.clientToken) : '';
  const paymentLink = quote.clientToken ? clientPaymentLink(publicOrigin, quote.clientToken) : '';
  const copyLink = async (kind: 'quote' | 'payment', link: string) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(kind);
      window.setTimeout(() => setLinkCopied(''), 2000);
    } catch (_) {
      setNote('The link could not be copied.');
    }
  };
  const showAccount = quote.status === 'accepted' || (summary?.receivedMinor || 0) > 0;
  const paidMinor = summary?.receivedMinor || 0;
  const stillMinor = summary?.outstandingMinor ?? snapshot.quoteTotalMinor;
  const depositOwedMinor = summary?.depositDueMinor || 0;
  const depositText = formatQuoteMoney(snapshot.depositMinor, 'R');
  let accountSentence = `A deposit of ${depositText} (${snapshot.depositPercent}%) secures the date. The balance is payable on completion of the shoot.`;
  if (showAccount) {
    if (summary && summary.outstandingMinor === 0 && paidMinor > 0) accountSentence = 'Paid in full.';
    else if (depositOwedMinor > 0) accountSentence = `Deposit of ${formatQuoteMoney(depositOwedMinor, 'R')} not yet received. The balance is payable on completion of the shoot.`;
    else accountSentence = 'Deposit received. The balance is payable on completion of the shoot.';
  }
  const canRecord = showPayments && quote.status === 'accepted' && (summary?.outstandingMinor || 0) > 0;
  const fillReceipt = (minor: number) => setReceiptAmount((minor / 100).toFixed(2));
  const [askReceiptMail, setAskReceiptMail] = useState(false);
  const saveReceipt = async (notifyClient: boolean) => {
    if (!receiptAmount.trim()) {
      setNote('Enter the amount received.');
      return;
    }
    setAskReceiptMail(false);
    const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/receipts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: receiptAmount.trim(),
        paidAt: receiptDate || undefined,
        note: receiptNote.trim(),
        notifyClient,
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setNote(body.error || 'The payment could not be saved.');
      return;
    }
    setReceiptAmount('');
    setReceiptDate('');
    setReceiptNote('');
    const mailed = notifyClient
      ? (body.notice?.status === 'failed' ? ' The client email failed.' : ' The client was emailed.')
      : ' The client was not emailed.';
    setNote(`Payment recorded.${mailed} Still to pay ${formatQuoteMoney(body.payments?.outstandingMinor || 0, 'R')}.`);
    onReceiptRecorded?.();
  };

  useEffect(() => {
    if (askForReceipt > 0) setReceiptOpen(true);
  }, [askForReceipt]);

  useEffect(() => {
    setGuideIds(Array.isArray(quote.guideIds) ? quote.guideIds.map(String) : []);
  }, [quote.id, quote.guideIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/studio/guides`, { credentials: 'include' });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || cancelled) return;
        const rows = Array.isArray(body.guides) ? body.guides : [];
        setGuideLibrary(rows.map((row: { id?: string; title?: string }) => ({
          id: String(row.id || ''),
          title: String(row.title || 'Guide'),
        })).filter((row: { id: string }) => row.id));
      } catch (_) {
        if (!cancelled) setGuideLibrary([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const canEditGuides = showGuides && ['draft', 'issued', 'accepted'].includes(quote.status);
  const canEditQuote = quoteEditable && ['draft', 'issued', 'accepted'].includes(quote.status) && !quote.deletedAt;
  const canIssue = showQuoteArticle && quote.status === 'draft' && !quote.deletedAt;
  const discountMinor = canEditQuote ? preview.quoteDiscountMinor : snapshot.quoteDiscountMinor;
  const priceMinor = canEditQuote ? preview.quoteTotalMinor : snapshot.quoteTotalMinor;
  const depositMinor = canEditQuote ? preview.depositMinor : snapshot.depositMinor;
  const balanceMinor = canEditQuote ? preview.balanceMinor : snapshot.balanceMinor;
  const settleMinor = canEditQuote ? preview.zarTotalMinor : snapshot.zarTotalMinor;
  const quotedStillMinor = canEditQuote && preview.zarTotalMinor !== Math.round(Number(snapshot.zarTotalMinor) || 0)
    ? Math.max(0, preview.zarTotalMinor - paidMinor)
    : stillMinor;
  const termsText = quote.acceptedTerms && canEditQuote
    ? termsWithDeposit(quote.acceptedTerms, preview.depositPercent)
    : quote.acceptedTerms;

  const letter = (quote.client?.message || quote.draft?.personalMessage || '').trim();
  const askedDate = formatPreferredDate(quote.preferredDate);
  const askedPlace = placeLine(quote.preferredLocation);
  const dayPlaces = dayPlaceLines(quote.sessions);
  const shootLabel = clientFacingShootType(quote.shootType);
  const packages = snapshot.lines.filter((line) => line.kind === 'package');
  const sessions = snapshot.lines.filter((line) => line.kind === 'session');
  const included = snapshot.lines.filter((line) => line.kind !== 'package' && line.kind !== 'session' && line.complimentary);
  const extras = snapshot.lines.filter((line) => line.kind === 'extra' && !line.complimentary);
  const custom = snapshot.lines.filter((line) => line.kind === 'custom' && !line.complimentary);
  const ungrouped = snapshot.lines.filter((line) => !line.kind);
  const payments = summary?.payments || [];
  const issued = studioDate(quote.issuedAt);

  const dirty = dirtyRef.current;
  const sessionLines = sessions.length > 0;

  const adoptForm = (next: QuoteFormState) => {
    const key = formKey(next);
    formRef.current = next;
    baselineRef.current = key;
    dirtyRef.current = false;
    setForm(next);
    setBaseline(key);
  };

  useEffect(() => {
    setEditingTimeId(null);
    setEditingWhenTime(false);
    setExpandedDayId(null);
  }, [quote.id]);

  useEffect(() => {
    if (dirtyRef.current) return;
    const next = formFromQuote(quote);
    const key = formKey(next);
    if (key === baselineRef.current) return;
    adoptForm(next);
  }, [quote]);

  const applySavedQuote = (saved: StoredQuote, message: string) => {
    adoptForm(formFromQuote(saved));
    setEditingTimeId(null);
    setEditingWhenTime(false);
    setExpandedDayId(null);
    setEditError('');
    setNote(message);
    onQuoteUpdated?.(saved);
  };

  const discardForm = () => {
    adoptForm(formFromQuote(quoteRef.current));
    setEditingTimeId(null);
    setEditingWhenTime(false);
    setExpandedDayId(null);
    setEditError('');
  };

  const rebuildSnapshot = async (nextLines: PricedLineInput[], extrasPatch: {
    discountPercent?: number;
    depositPercent?: number;
  } = {}): Promise<QuotePricingSnapshot> => {
    const settings = loadCurrencySettings();
    return priceQuote({
      lines: nextLines,
      currency: snapshot.currency || 'R',
      discountPercent: extrasPatch.discountPercent ?? snapshot.discountPercent ?? 0,
      depositPercent: extrasPatch.depositPercent ?? snapshot.depositPercent ?? 50,
      convert: (amount, from, to) => convertCurrencyWithMarkup(amount, from, to, settings),
    });
  };

  const persistDetails = async (
    patch: {
      letter?: string;
      preferredDate?: string;
      preferredTime?: string;
      preferredLocation?: { name?: string; address?: string; mapsUrl?: string };
      snapshot?: QuotePricingSnapshot;
      sessions?: QuoteSession[];
      depositOnly?: number;
    },
    message: string,
  ) => {
    if (patch.depositOnly != null && quote.status !== 'draft') {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/payment-terms`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ depositPercent: patch.depositOnly }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.id || !body?.snapshot) {
        throw new Error(body.error || 'The deposit could not be saved.');
      }
      applySavedQuote(body as StoredQuote, message);
      return;
    }

    const nextSnapshot = patch.snapshot || snapshot;
    const nextLetter = patch.letter != null ? patch.letter : (quote.client?.message || '');
    const nextSessions = patch.sessions !== undefined
      ? patch.sessions
      : syncSessionsFromLines(quote.sessions, nextSnapshot.lines);
    const packageLine = nextSnapshot.lines.find((line) => line.kind === 'package' && !line.complimentary);
    const draft = quote.draft && typeof quote.draft === 'object'
      ? {
        ...quote.draft,
        personalMessage: nextLetter,
        discountPercent: nextSnapshot.discountPercent,
        depositPercent: nextSnapshot.depositPercent,
        preferredDate: patch.preferredDate ?? quote.preferredDate ?? quote.draft.preferredDate,
        preferredTime: patch.preferredTime ?? quote.preferredTime ?? quote.draft.preferredTime,
        preferredLocation: patch.preferredLocation ?? quote.preferredLocation ?? quote.draft.preferredLocation,
        sessions: nextSessions || quote.draft.sessions,
        ...(packageLine
          ? {
            overridePackagePrice: fromMinor(packageLine.sourceUnitMinor || packageLine.quoteUnitMinor),
            overridePackageCurrency: packageLine.sourceCurrency || nextSnapshot.currency,
          }
          : {}),
      }
      : quote.draft;

    const payload = {
      client: {
        name: quote.client?.name || '',
        email: quote.client?.email || '',
        company: quote.client?.company || '',
        billingAddress: quote.client?.billingAddress || '',
        countryCode: quote.client?.countryCode || '',
        phone: quote.client?.phoneE164 || '',
        preference: quote.client?.preference || 'either',
        message: nextLetter,
      },
      shootType: quote.shootType || '',
      preferredDate: patch.preferredDate ?? quote.preferredDate ?? '',
      preferredTime: patch.preferredTime ?? quote.preferredTime ?? '',
      preferredLocation: patch.preferredLocation ?? quote.preferredLocation ?? { name: '', address: '', mapsUrl: '' },
      sessions: nextSessions || quote.sessions || [],
      adhocPackage: quote.adhocPackage || null,
      guideIds: quote.guideIds || [],
      snapshot: nextSnapshot,
      draft,
    };

    const endpoint = quote.status === 'draft'
      ? `${API_BASE_URL}/quotes/${quote.id}`
      : `${API_BASE_URL}/quotes/${quote.id}/details`;
    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || !body?.id || !body?.snapshot) {
      throw new Error(body.error || 'The quote could not be updated.');
    }
    applySavedQuote(body as StoredQuote, message);
  };

  const saveQuote = async (): Promise<boolean> => {
    if (!canEditQuote || busyRef.current) return false;
    const currentForm = formRef.current;
    const discountPercent = Number(currentForm.discount);
    const depositPercent = Number(currentForm.deposit);
    if (!Number.isFinite(discountPercent) || discountPercent < 0 || discountPercent > 100) {
      editErrorRef.current = 'Enter a discount between 0 and 100.';
      setEditError(editErrorRef.current);
      return false;
    }
    if (!Number.isFinite(depositPercent) || depositPercent < 0 || depositPercent > 100) {
      editErrorRef.current = 'Enter a deposit between 0 and 100.';
      setEditError(editErrorRef.current);
      return false;
    }
    const nextLines: PricedLineInput[] = [];
    for (const line of snapshot.lines) {
      const draft = currentForm.lines.find((entry) => entry.id === line.id);
      const input = draft ? draftToInput(line, draft) : lineToInput(line);
      if (!input) {
        editErrorRef.current = 'Check the prices and durations. Each one needs a valid number.';
        setEditError(editErrorRef.current);
        return false;
      }
      nextLines.push(input);
    }
    setEditBusy(true);
    busyRef.current = true;
    setEditError('');
    try {
      const next = await rebuildSnapshot(nextLines, { discountPercent, depositPercent });
      let nextSessions = sessionsWithPlaces(quote.sessions, next.lines, currentForm.lines);
      if (!nextSessions.length && sessions.length) {
        nextSessions = sessions.map((line) => {
          const draft = currentForm.lines.find((entry) => entry.id === line.id);
          return {
            id: line.sessionId || line.id,
            label: draft?.name.trim() || line.name,
            date: draft?.date.trim() || line.date || '',
            startTime: draft?.time.trim() || line.startTime || '',
            durationMinutes: draft && draft.duration.trim() !== '' ? Number(draft.duration) : (line.durationMinutes || 0),
            location: normalizeShootLocation(draft?.location),
            notes: draft?.description.trim() || line.description || '',
            priceMajor: fromMinor(line.quoteExtendedMinor),
          };
        });
      }
      const preferred = nextSessions.length
        ? preferredFromSessions(nextSessions)
        : {
          preferredDate: currentForm.whenDate.trim(),
          preferredTime: currentForm.whenTime.trim(),
          preferredLocation: normalizeShootLocation(currentForm.whenLocation),
        };
      await persistDetails({
        letter: currentForm.letter,
        snapshot: next,
        sessions: nextSessions,
        preferredDate: preferred.preferredDate,
        preferredTime: preferred.preferredTime,
        preferredLocation: normalizeShootLocation(preferred.preferredLocation),
      }, 'Quotation saved.');
      return true;
    } catch (error) {
      editErrorRef.current = error instanceof Error ? error.message : 'The quote could not be updated.';
      setEditError(editErrorRef.current);
      return false;
    } finally {
      busyRef.current = false;
      setEditBusy(false);
    }
  };

  const saveRef = useRef(saveQuote);
  const discardRef = useRef(discardForm);
  saveRef.current = saveQuote;
  discardRef.current = discardForm;

  useEffect(() => {
    if (!showQuoteArticle || !canEditQuote) {
      onEditorChange?.(null);
      return undefined;
    }
    onEditorChange?.({
      get dirty() { return dirtyRef.current; },
      get busy() { return busyRef.current; },
      get error() { return editErrorRef.current; },
      save: () => saveRef.current(),
      discard: () => discardRef.current(),
    });
    return undefined;
  }, [showQuoteArticle, canEditQuote, dirty, editBusy, editError, onEditorChange]);

  useEffect(() => () => onEditorChange?.(null), [onEditorChange]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [dirty]);

  const updateLine = (id: string, patch: Partial<LineDraft>) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.id === id ? { ...line, ...patch } : line)),
    }));
  };

  const issueQuote = async () => {
    if (!canIssue || issueBusy) return;
    if (dirtyRef.current) {
      const saved = await saveQuote();
      if (!saved) return;
    }
    setIssueBusy(true);
    setNote('');
    try {
      const response = await fetch(`${API_BASE_URL}/quotes/${quote.id}/issue`, {
        method: 'POST',
        credentials: 'include',
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.id) {
        setNote(body.error || 'The quotation could not be issued.');
        return;
      }
      setNote('Quotation issued.');
      onQuoteUpdated?.(body as StoredQuote);
    } catch (_) {
      setNote('The quotation could not be issued.');
    } finally {
      setIssueBusy(false);
    }
  };

  const saveGuides = async () => {
    if (!canEditGuides) return;
    setGuidesBusy('save');
    setNote('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/guides`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ guideIds }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setNote(body.error || 'The guides could not be saved.');
        return;
      }
      setNote(guideIds.length
        ? 'Guides saved. The client can open them on their quotation page.'
        : 'Guides cleared on this quotation.');
      onGuidesUpdated?.();
    } catch (_) {
      setNote('The guides could not be saved.');
    } finally {
      setGuidesBusy('');
    }
  };

  const emailGuides = async () => {
    if (!guideIds.length) {
      setNote('Choose at least one guide to email.');
      return;
    }
    if (!quote.client?.email) {
      setNote('Add an email address before sending guides.');
      return;
    }
    setGuidesBusy('email');
    setNote('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/guides/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          to: quote.client.email,
          clientName: quote.client.name || '',
          guideIds,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setNote(body.error || 'The guides could not be emailed.');
        return;
      }
      if (body.status === 'sent') setNote(`Guides sent to ${body.destination}. The email provider accepted it. Delivery is not confirmed.`);
      else if (body.status === 'captured') setNote('Email is not configured, so the guides were not sent.');
      else setNote(body.error || 'The guides could not be emailed.');
    } catch (_) {
      setNote('The guides could not be emailed.');
    } finally {
      setGuidesBusy('');
    }
  };

  const whatsAppGuides = async () => {
    if (!guideIds.length) {
      setNote('Choose at least one guide to send on WhatsApp.');
      return;
    }
    if (!quote.client?.phoneE164) {
      setNote('Add a mobile number before sending guides on WhatsApp.');
      return;
    }
    setGuidesBusy('whatsapp');
    setNote('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/guides/whatsapp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          countryCode: quote.client.countryCode || '+27',
          phone: quote.client.phoneE164,
          clientName: quote.client.name || '',
          guideIds,
          publicOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setNote(body.error || 'WhatsApp could not be prepared.');
        return;
      }
      if (!body.whatsAppUrl) {
        setNote('WhatsApp could not be prepared.');
        return;
      }
      const handoff = openWhatsAppHandoff(body.whatsAppUrl);
      setNote(
        handoff.opened
          ? 'WhatsApp opened with guide links. Confirm sending in WhatsApp. Links stay open for about 30 days.'
          : handoff.message,
      );
    } catch (_) {
      setNote('WhatsApp could not be prepared.');
    } finally {
      setGuidesBusy('');
    }
  };

  const downloadPdf = async () => {
    setPdfBusy('download');
    setNote('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/document.pdf`, {
        headers: { Accept: 'application/pdf' },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setNote(body.error || 'The PDF could not be prepared.');
        return;
      }
      await downloadResponseFile(response, `${quote.quoteNumber || 'quotation'}.pdf`);
    } catch (error) {
      setNote('The PDF could not be prepared.');
    } finally {
      setPdfBusy('');
    }
  };
  const emailPdf = async () => {
    if (!quote.client?.email) {
      setNote('Add an email address before sending the PDF.');
      return;
    }
    setPdfBusy('email');
    setNote('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/document-email`, { method: 'POST' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setNote(body.error || 'The PDF could not be sent.');
        return;
      }
      if (body.status === 'sent') setNote(`PDF sent to ${body.destination}. The email provider accepted it. Delivery is not confirmed.`);
      else if (body.status === 'captured') setNote('Email is not configured, so the PDF was not sent. Download it and send the file yourself.');
      else setNote(body.error || 'The PDF could not be sent. Download it and send the file yourself.');
    } catch (error) {
      setNote('The PDF could not be sent. Download it and send the file yourself.');
    } finally {
      setPdfBusy('');
    }
  };

  const collapseDay = (id: string) => {
    setExpandedDayId((current) => (current === id ? null : current));
    setEditingTimeId((current) => (current === id ? null : current));
  };

  const renderLines = (lines: PricedLine[], heading?: string) => {
    if (!lines.length) return null;
    const collapseDays = canEditQuote && lines.length > 1 && lines.every((entry) => entry.kind === 'session');
    return (
      <div className="studio-quote-group">
        {heading ? <h3>{heading}</h3> : null}
        {lines.map((line) => {
          const detail = lineDetail(line);
          const draft = form.lines.find((entry) => entry.id === line.id);
          const dayCollapsed = collapseDays && expandedDayId !== line.id;
          const open = Boolean(canEditQuote && draft && !dayCollapsed);
          const summary = line.kind === 'session' ? daySummary(line, draft, quote.sessions) : null;
          return (
            <div className={`studio-quote-line${line.kind === 'session' ? ' is-day' : ''}${dayCollapsed ? ' is-collapsed' : ''}${open ? ' is-editing' : ''}`} key={line.id} data-testid={line.kind === 'session' ? `quote-day-${line.id}` : undefined}>
              <div className="studio-quote-line-content">
                {dayCollapsed && summary ? (
                  <div className="studio-quote-day-summary">
                    <div className="studio-quote-line-head">
                      <strong>{summary.name}</strong>
                      <button
                        type="button"
                        className="studio-text-action"
                        aria-expanded="false"
                        onClick={() => setExpandedDayId(line.id)}
                        disabled={editBusy}
                      >
                        Edit
                      </button>
                    </div>
                    <span className="studio-quote-meta">{summary.detail}</span>
                    {summary.description ? <em className="studio-quote-note">{summary.description}</em> : null}
                    {summary.features.length > 0 ? (
                      <ul className="studio-quote-features">
                        {summary.features.map((feature) => <li key={feature}>{feature}</li>)}
                      </ul>
                    ) : null}
                  </div>
                ) : open && draft ? (
                  <div className="studio-quote-inline-edit">
                    {line.kind === 'session' && collapseDays ? (
                      <div className="studio-quote-edit-wide studio-quote-day-bar">
                        <span>{draft.name.trim() || line.name}</span>
                        <button
                          type="button"
                          className="studio-text-action"
                          aria-expanded="true"
                          onClick={() => collapseDay(line.id)}
                          disabled={editBusy}
                        >
                          Done
                        </button>
                      </div>
                    ) : null}
                    <label className="studio-quote-edit-field">
                      Name
                      <input aria-label="Line name" value={draft.name} onChange={(event) => updateLine(line.id, { name: event.target.value })} disabled={editBusy} />
                    </label>
                    <label className="studio-quote-edit-field">
                      Price ({snapshot.currency})
                      <input
                        aria-label="Line price"
                        inputMode="decimal"
                        value={draft.price}
                        onChange={(event) => updateLine(line.id, { price: event.target.value })}
                        disabled={editBusy}
                        placeholder={line.complimentary ? 'Included — enter a price to charge' : ''}
                      />
                    </label>
                    {(line.kind === 'package' || line.kind === 'session') ? (
                      <label className="studio-quote-edit-field">
                        Duration (minutes)
                        <input aria-label="Duration minutes" inputMode="numeric" value={draft.duration} onChange={(event) => updateLine(line.id, { duration: event.target.value })} disabled={editBusy} />
                      </label>
                    ) : null}
                    {line.kind === 'session' ? (
                      <>
                        <label className="studio-quote-edit-field">
                          Date
                          <input type="date" aria-label="Session date" value={draft.date} onChange={(event) => updateLine(line.id, { date: event.target.value })} disabled={editBusy} />
                        </label>
                        {editingTimeId === line.id ? (
                          <ClockTimeField
                            className="studio-quote-edit-field"
                            label="Start time"
                            ariaLabel="Session start time"
                            value={draft.time}
                            onChange={(value) => updateLine(line.id, { time: value })}
                            disabled={editBusy}
                            autoOpen
                            onDismiss={() => setEditingTimeId(null)}
                          />
                        ) : (
                          <div className="studio-quote-edit-field">
                            <span>Start time</span>
                            <div className="studio-quote-time-rest">
                              <span>{draft.time || 'No time yet'}</span>
                              <button
                                type="button"
                                className="studio-text-action"
                                onClick={() => setEditingTimeId(line.id)}
                                disabled={editBusy}
                              >
                                {draft.time ? 'Edit' : 'Set a time'}
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="studio-quote-edit-field studio-quote-edit-wide" data-testid="session-location-edit">
                          <ShootLocationField
                            value={draft.location}
                            onChange={(location) => updateLine(line.id, { location })}
                            label="Place"
                            placeLabel="This day has a place"
                            laterLabel="Decide later"
                            help="This place is only for this day."
                            openPlaceFields
                          />
                        </div>
                      </>
                    ) : null}
                    <label className="studio-quote-edit-field studio-quote-edit-wide">
                      Description
                      <textarea aria-label="Line description" rows={3} value={draft.description} onChange={(event) => updateLine(line.id, { description: event.target.value })} disabled={editBusy} />
                    </label>
                    {(line.kind === 'package' || (line.features && line.features.length > 0) || draft.features) ? (
                      <label className="studio-quote-edit-field studio-quote-edit-wide">
                        Included (one per line)
                        <textarea aria-label="Line features" rows={4} value={draft.features} onChange={(event) => updateLine(line.id, { features: event.target.value })} disabled={editBusy} />
                      </label>
                    ) : null}
                  </div>
                ) : (
                  <>
                    <strong>{line.name}</strong>
                    {detail ? <span className="studio-quote-meta">{detail}</span> : null}
                    {line.description ? <em className="studio-quote-note">{line.description}</em> : null}
                    {line.features && line.features.length > 0 ? (
                      <ul className="studio-quote-features">
                        {line.features.map((feature) => <li key={feature}>{feature}</li>)}
                      </ul>
                    ) : null}
                  </>
                )}
              </div>
              <div className="studio-quote-line-value">
                <span>{dayCollapsed ? dayAmount(line, draft, snapshot.currency) : (line.complimentary && !(draft && draft.price.trim()) ? 'Included' : formatQuoteMoney(line.quoteExtendedMinor, snapshot.currency))}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <section className="studio-record-panel" aria-label="Quotation and payments" data-testid="quote-money-panel">
      {note ? <p className="studio-note">{note}</p> : null}

      {variant === 'deposit' ? (
        <section className="studio-panel" data-testid="deposit-payment-panel" aria-labelledby="deposit-payment-heading">
          <h3 id="deposit-payment-heading" className="studio-section-label">Deposit</h3>
          <p className="studio-note" data-testid="money-sentence">{accountSentence}</p>
          <div className="studio-quote-totals" data-testid="quote-figures">
            <div>
              <span>Deposit ({snapshot.depositPercent}%)</span>
              <strong>{formatQuoteMoney(snapshot.depositMinor, 'R')}</strong>
            </div>
            <div>
              <span>Balance</span>
              <strong>{formatQuoteMoney(snapshot.balanceMinor, 'R')}</strong>
            </div>
            <div className="is-account">
              <span>Paid so far</span>
              <strong>{formatQuoteMoney(paidMinor, 'R')}</strong>
            </div>
            <div>
              <span>Still to pay</span>
              <strong>{formatQuoteMoney(stillMinor, 'R')}</strong>
            </div>
          </div>
          {canRecord && paymentLink ? (
            <div className="studio-toolbar">
              <button type="button" className="studio-text-action" onClick={() => copyLink('payment', paymentLink)} data-testid="copy-payment-link">
                {linkCopied === 'payment' ? 'Payment link copied' : 'Copy payment link'}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {showQuoteArticle ? (
      <article className={`studio-original-quote is-editable-quote${canEditQuote ? ' is-open-form' : ''}`} data-testid="original-quote">
        <header className="studio-quote-mast">
          <p className="studio-section-label">Quotation</p>
          <p className="studio-quote-title">{packageName}</p>
          <p className="studio-quote-ref">
            {quote.quoteNumber}
            {issued ? ` · Issued ${issued}` : quote.status === 'draft' ? ' · Draft' : ''}
            {quote.externalReference ? ` · ${quote.externalReference}` : ''}
          </p>
          {shootLabel && shootLabel !== packageName ? <p className="studio-quote-ref">{shootLabel}</p> : null}
        </header>

        {editError ? <p className="studio-note" role="alert">{editError}</p> : null}

        <div className="studio-quote-group studio-quote-letter-block is-editing">
          <h3>Personal message</h3>
          <div className="studio-quote-field-row">
            <div className="studio-quote-field-content">
              {canEditQuote ? (
                <div className="studio-quote-inline-edit studio-quote-letter-composer form-section">
                  <label htmlFor={`quote-letter-${quote.id}`}>Personal message (optional)</label>
                  <LetterComposer
                    id={`quote-letter-${quote.id}`}
                    value={form.letter}
                    onChange={(value) => setForm((current) => ({ ...current, letter: value }))}
                  />
                  <small className="form-help">Bold, italic, underline, bullets, numbering, and text size appear on the client quotation, the preview, and the PDF. A blank line starts a new paragraph. Page break, a line with ---, or two blank lines together, starts a new page.</small>
                </div>
              ) : letter ? (
                <QuoteLetter text={letter} />
              ) : (
                <p className="studio-note">No personal message yet.</p>
              )}
            </div>
          </div>
        </div>

        {canEditQuote && !sessionLines ? (
          <div className="studio-quote-field-row studio-quote-when-row is-editing">
            <div className="studio-quote-field-content">
              <div className="studio-quote-inline-edit studio-quote-inline-when studio-quote-when-composer">
                <div className="form-section studio-quote-edit-wide">
                  <PreferredDateField
                    value={form.whenDate}
                    onChange={(value) => setForm((current) => ({ ...current, whenDate: value }))}
                    label="Preferred shoot date"
                    dateLabel="Client has a date"
                    laterLabel="Decide later"
                  />
                </div>
                <div className="form-section studio-quote-edit-wide">
                  {editingWhenTime ? (
                    <ClockTimeField
                      label="Preferred start time"
                      ariaLabel="Preferred start time"
                      value={form.whenTime}
                      onChange={(value) => setForm((current) => ({ ...current, whenTime: value }))}
                      autoOpen
                      onDismiss={() => setEditingWhenTime(false)}
                    />
                  ) : (
                    <div className="studio-quote-time-rest">
                      <span>{form.whenTime ? `Start time ${form.whenTime}` : 'No start time yet'}</span>
                      <button type="button" className="studio-text-action" onClick={() => setEditingWhenTime(true)}>
                        {form.whenTime ? 'Edit time' : 'Set a time'}
                      </button>
                    </div>
                  )}
                </div>
                <div className="form-section studio-quote-edit-wide">
                  <ShootLocationField
                    value={form.whenLocation}
                    onChange={(location) => setForm((current) => ({ ...current, whenLocation: location }))}
                    label="Shoot location"
                    placeLabel="Client has a place"
                    laterLabel="Decide later"
                    help="Search for the place, or type it below. Choosing a result saves a Google Maps pin."
                  />
                </div>
              </div>
            </div>
          </div>
        ) : !canEditQuote ? (
          <div className="studio-quote-field-row studio-quote-when-row">
            <div className="studio-quote-field-content">
              <div className="studio-quote-when">
                <span>As quoted</span>
                {dayPlaces.length > 1 ? (
                  <div className="studio-quote-day-places" data-testid="quote-day-places">
                    {dayPlaces.map((line) => <span key={line}>{line}</span>)}
                  </div>
                ) : askedDate || askedPlace ? (
                  <>
                    {askedDate ? `${askedDate}${quote.preferredTime ? ` at ${quote.preferredTime}` : ''}` : ''}
                    {askedDate && askedPlace ? ' · ' : ''}
                    {askedPlace}
                  </>
                ) : (
                  <em>Date and place to be decided</em>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {renderLines(packages, packages.length > 1 ? 'Packages' : 'Package')}
        {renderLines(sessions, sessions.length > 1 ? 'Days' : 'Day')}
        {renderLines(included, 'Included')}
        {renderLines(extras, 'Extras')}
        {renderLines(custom, 'Additional services')}
        {renderLines(ungrouped)}

        <div className="studio-quote-totals" data-testid="quote-figures">
          <div>
            <span>Subtotal</span>
            <strong>{formatQuoteMoney(snapshot.quoteSubtotalMinor, snapshot.currency)}</strong>
          </div>
          <div className={`studio-quote-total-row${canEditQuote ? ' is-editing' : ''}`}>
            <span className={canEditQuote ? 'studio-quote-adjust' : undefined}>
              <span>Discount{canEditQuote ? '' : ` (${snapshot.discountPercent}%)`}</span>
              {canEditQuote ? (
                <label className="studio-quote-percent">
                  <input
                    aria-label="Discount percent"
                    inputMode="decimal"
                    value={form.discount}
                    onChange={(event) => setForm((current) => ({ ...current, discount: event.target.value }))}
                    disabled={editBusy}
                  />
                  <span>%</span>
                </label>
              ) : null}
            </span>
            <strong>
              {discountMinor > 0
                ? `-${formatQuoteMoney(discountMinor, snapshot.currency)}`
                : formatQuoteMoney(0, snapshot.currency)}
            </strong>
          </div>
          <div className="is-total">
            <span>Price</span>
            <strong>{formatQuoteMoney(priceMinor, snapshot.currency)}</strong>
          </div>
          <div className={`studio-quote-total-row${canEditQuote ? ' is-editing' : ''}`}>
            <span className={canEditQuote ? 'studio-quote-adjust' : undefined}>
              <span>Deposit{canEditQuote ? '' : ` (${snapshot.depositPercent}%)`}</span>
              {canEditQuote ? (
                <label className="studio-quote-percent">
                  <input
                    aria-label="Deposit percent"
                    inputMode="decimal"
                    value={form.deposit}
                    onChange={(event) => setForm((current) => ({ ...current, deposit: event.target.value }))}
                    disabled={editBusy}
                  />
                  <span>%</span>
                </label>
              ) : null}
            </span>
            <strong>{formatQuoteMoney(depositMinor, 'R')}</strong>
          </div>
          <div>
            <span>Balance</span>
            <strong>{formatQuoteMoney(balanceMinor, 'R')}</strong>
          </div>
          {showAccount ? (
            <>
              <div className="is-account">
                <span>Paid so far</span>
                <strong>{formatQuoteMoney(paidMinor, 'R')}</strong>
              </div>
              <div>
                <span>Still to pay</span>
                <strong>{formatQuoteMoney(quotedStillMinor, 'R')}</strong>
              </div>
            </>
          ) : null}
        </div>

        {snapshot.currency !== 'R' ? (
          <p className="studio-quote-terms">
            Priced in {snapshot.currency}. Card payments settle at {formatQuoteMoney(settleMinor, 'R')}.
          </p>
        ) : null}
        {termsText ? <p className="studio-quote-terms">{termsText}</p> : null}
      </article>
      ) : null}

      {variant === 'full' ? (
        <p className="studio-note" data-testid="money-sentence">{accountSentence}</p>
      ) : null}

      {showGuides && (canEditGuides || guideLibrary.length > 0) ? (
        <section className="studio-quote-guides" data-testid="quote-guides-panel" aria-labelledby="quote-guides-heading">
          <h3 id="quote-guides-heading">Client guides</h3>
          <p className="studio-note">
            {canEditGuides
              ? 'Include PDFs on this quotation at any time, including after it is issued or accepted. The client opens them from their private link. You can also email or WhatsApp the selected files.'
              : 'Guides on this quotation.'}
          </p>
          {guideLibrary.length === 0 ? (
            <p className="studio-note">No guides in the library yet. Add them under Settings → Client guides.</p>
          ) : (
            <ul className="studio-quote-guides-list">
              {guideLibrary.map((guide) => {
                const checked = guideIds.includes(guide.id);
                return (
                  <li key={guide.id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!canEditGuides || guidesBusy !== ''}
                        onChange={() => {
                          setGuideIds((current) => (
                            checked ? current.filter((id) => id !== guide.id) : current.concat([guide.id])
                          ));
                        }}
                      />
                      <span>{guide.title}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {canEditGuides && guideLibrary.length > 0 ? (
            <div className="studio-toolbar" style={{ flexWrap: 'wrap' }}>
              <button type="button" className="studio-secondary" onClick={saveGuides} disabled={guidesBusy !== ''} data-testid="save-quote-guides">
                {guidesBusy === 'save' ? 'Saving…' : 'Save guides on quotation'}
              </button>
              <button type="button" className="studio-secondary" onClick={emailGuides} disabled={guidesBusy !== '' || !guideIds.length} data-testid="email-quote-guides">
                {guidesBusy === 'email' ? 'Sending…' : 'Email selected guides'}
              </button>
              <button type="button" className="studio-secondary" onClick={whatsAppGuides} disabled={guidesBusy !== '' || !guideIds.length} data-testid="whatsapp-quote-guides">
                {guidesBusy === 'whatsapp' ? 'Preparing…' : 'WhatsApp selected guides'}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {showQuoteArticle ? (
      <div className="studio-toolbar" style={{ flexWrap: 'wrap' }}>
        {canIssue ? (
          <button type="button" className="studio-primary" onClick={issueQuote} disabled={issueBusy} data-testid="issue-quote-inline">
            {issueBusy ? 'Issuing…' : 'Issue quotation'}
          </button>
        ) : null}
        <button type="button" className="studio-secondary" onClick={downloadPdf} disabled={pdfBusy !== ''} data-testid="download-quote-pdf">
          {pdfBusy === 'download' ? 'Preparing PDF…' : 'Download PDF'}
        </button>
        <button type="button" className="studio-secondary" onClick={emailPdf} disabled={pdfBusy !== ''} data-testid="email-quote-pdf">
          {pdfBusy === 'email' ? 'Sending PDF…' : 'Email PDF'}
        </button>
        {quoteLink ? (
          <button type="button" className="studio-text-action" onClick={() => copyLink('quote', quoteLink)}>
            {linkCopied === 'quote' ? 'Client link copied' : 'Copy client link'}
          </button>
        ) : null}
      </div>
      ) : null}

      {canRecord ? (
        <details
          className="studio-record-payment"
          data-testid="record-payment"
          open={receiptOpen}
          onToggle={(event) => setReceiptOpen(event.currentTarget.open)}
        >
          <summary>Record a payment</summary>
          <p className="studio-note">For an EFT, cash or any payment received outside the website.</p>
          <div className="studio-toolbar" style={{ flexWrap: 'wrap' }}>
            {depositOwedMinor > 0 ? (
              <button type="button" className="studio-secondary" onClick={() => fillReceipt(depositOwedMinor)}>
                Deposit {formatQuoteMoney(depositOwedMinor, 'R')}
              </button>
            ) : null}
            <button type="button" className="studio-secondary" onClick={() => fillReceipt(summary?.outstandingMinor || 0)}>
              Everything owing {formatQuoteMoney(summary?.outstandingMinor || 0, 'R')}
            </button>
          </div>
          <div className="studio-toolbar" style={{ flexWrap: 'wrap' }}>
            <input aria-label="Receipt amount in rand" inputMode="decimal" placeholder="Amount (rand)" value={receiptAmount} onChange={(event) => setReceiptAmount(event.target.value)} />
            <input aria-label="Date paid" type="date" value={receiptDate} onChange={(event) => setReceiptDate(event.target.value)} />
            <input aria-label="Receipt note" placeholder="EFT, cash, reference" value={receiptNote} onChange={(event) => setReceiptNote(event.target.value)} />
            <button
              type="button"
              className="studio-primary"
              onClick={() => {
                if (!receiptAmount.trim()) {
                  setNote('Enter the amount received.');
                  return;
                }
                if (!quote.client?.email) {
                  void saveReceipt(false);
                  return;
                }
                setAskReceiptMail(true);
              }}
            >
              Record payment
            </button>
          </div>
        </details>
      ) : null}

      {showPayments && payments.length > 0 ? (
        <>
          <h3 className="studio-section-label">Payments</h3>
          {payments.map((payment) => (
            <p key={payment.id}>
              {paymentStateLabel(payment.type, payment.status)}
              {' · '}
              {formatQuoteMoney(payment.verifiedMinor || 0, 'R')}
              {payment.paidAt ? ` · ${studioDate(payment.paidAt)}` : ''}
              {payment.note ? ` · ${payment.note}` : ''}
            </p>
          ))}
        </>
      ) : null}

      {variant === 'full' && onOpenHistory ? (
        <div className="studio-quote-activity">
          <button type="button" className="studio-text-action" onClick={onOpenHistory} data-testid="open-job-history">
            Open job history
          </button>
        </div>
      ) : null}
      {askReceiptMail ? (
        <ClientSendPrompt
          title="Email the client?"
          detail="Record this payment, and choose whether the client is emailed about it."
          onSend={() => { void saveReceipt(true); }}
          onSkip={() => { void saveReceipt(false); }}
        />
      ) : null}

    </section>
  );
};

export default StudioQuoteRecord;
